/**
 * Store for test data visualization form state and actions.
 */
import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { lastDayOfMonth, startOfMonth, subMonths } from "date-fns"
import type { RootState } from "../store"

const KEYS = ["table", "timegraph"]

// Form fields that can be omitted and cause missing parameter errors.
// There are no freeform input fields yet that cause other validation errors.
const FORM_FIELDS = ["groups", "trainees", "tests", "selectedGroup"]

type FormErrors = Record<string, Record<string, boolean>>

const baseFormErrObj: FormErrors = {}
KEYS.forEach((key) => {
	baseFormErrObj[key] = {}
	FORM_FIELDS.forEach((field) => {
		const formErrors = baseFormErrObj[key]
		if (formErrors) formErrors[field] = false
	})
})

/* Selection/option fields hold loosely-typed domain objects consumed across many
 * form components; kept as any[] to match their prior (untyped) contracts. */
interface VisualizationState {
	isSubmitting: boolean
	selectedGroup: any
	selectedRootGroups: any[]
	selectedGroups: any[]
	selectedType: string
	selectedTests: any[]
	selectedSurvey: string
	selectedSurveys: any[]
	selectedTrainees: any[]
	selectedSex: string
	groupOptions: any[]
	testOptions: any[]
	traineeOptions: any[]
	averageValue: string
	disableDateRange: boolean
	startDate: string
	endDate: string
	ageGroupRange: number[]
	developmentAverage: boolean
	developmentComplete: boolean
	interval: string
	sampleAggregate: string
	showGroups: boolean
	showTrainees: boolean
	formErrors: FormErrors
	deltaData: any[]
	deltaProtocol: string
	deltaShowEmpty: boolean
	deltaAggregate: string
	targetTableDemo: string
	targetTableDemoPhv: string
	targetTableCategory: any
	selectedProfileId: any
	scrollToTestId: string
	scrollToGraph: string
}

const initialState: VisualizationState = {
	isSubmitting: false,
	selectedGroup: {},
	selectedRootGroups: [],
	selectedGroups: [],
	selectedType: "test",
	selectedTests: [],
	selectedSurvey: "",
	selectedSurveys: [],
	selectedTrainees: [],
	selectedSex: "",
	groupOptions: [],
	testOptions: [],
	traineeOptions: [],
	averageValue: "teamAverage",
	disableDateRange: false,
	startDate: startOfMonth(subMonths(new Date(), 6)).toISOString(),
	endDate: lastDayOfMonth(new Date()).toISOString(),
	ageGroupRange: [9, 15],
	developmentAverage: false,
	developmentComplete: false,
	interval: "biannual",
	sampleAggregate: "best",
	showGroups: true,
	showTrainees: true,
	formErrors: baseFormErrObj,
	deltaData: [],
	deltaProtocol: "",
	deltaShowEmpty: false,
	deltaAggregate: "average",
	// For target level table use; redux state changes caused by table clicks are applied to other components' local state when needed
	targetTableDemo: "P15",
	targetTableDemoPhv: "P> + 1",
	targetTableCategory: "ageGroup",
	selectedProfileId: "",
	scrollToTestId: "",
	scrollToGraph: "",
}

export const visualizationSlice = createSlice({
	name: "visualization",
	initialState,
	reducers: {
		setIsSubmitting(state, { payload }: PayloadAction<boolean>) {
			state.isSubmitting = payload
		},
		setSelectedRootGroups(state, { payload }: PayloadAction<unknown[]>) {
			state.selectedRootGroups = payload
		},
		setSelectedGroups(state, { payload }: PayloadAction<unknown[]>) {
			state.selectedGroups = payload
		},
		setSelectedType(state, { payload }: PayloadAction<string>) {
			state.selectedType = payload
		},
		setSelectedTests(state, { payload }: PayloadAction<unknown[]>) {
			state.selectedTests = payload
		},
		setSelectedSurveys(state, { payload }: PayloadAction<unknown[]>) {
			state.selectedSurveys = payload
		},
		setSelectedTrainees(state, { payload }: PayloadAction<any>) {
			state.selectedTrainees = payload
		},
		setSelectedSex(state, { payload }: PayloadAction<string>) {
			state.selectedSex = payload
		},
		setTestOptions(state, { payload }: PayloadAction<unknown[]>) {
			state.testOptions = payload
		},
		setDisableDateRange(state, { payload }: PayloadAction<boolean>) {
			state.disableDateRange = payload
		},
		setStartDate(state, { payload }: PayloadAction<string | undefined>) {
			if (payload !== undefined) state.startDate = payload
		},
		setEndDate(state, { payload }: PayloadAction<string | undefined>) {
			if (payload !== undefined) state.endDate = payload
		},
		setSampleAggregate(state, { payload }: PayloadAction<string>) {
			state.sampleAggregate = payload
		},
		setShowGroups(state, { payload }: PayloadAction<boolean>) {
			state.showGroups = payload
		},
		setShowTrainees(state, { payload }: PayloadAction<boolean>) {
			state.showTrainees = payload
		},
		resetFormErrors(state, action: PayloadAction<{ key?: string }>) {
			const key = (action as unknown as { key?: string }).key
			if (key) {
				const formErrors = state.formErrors[key]
				if (formErrors) FORM_FIELDS.forEach((field) => (formErrors[field] = false))
			} else state.formErrors = baseFormErrObj
		},
		setFormError(state, { payload }: PayloadAction<{ key: string; field: string; bool: boolean }>) {
			const { key, field, bool } = payload
			const formErrors = state.formErrors[key]
			if (formErrors) formErrors[field] = bool
		},
		setSelectedProfileId(state, { payload }: PayloadAction<any>) {
			state.selectedProfileId = payload
		},
		setScrollToTestId(state, { payload }: PayloadAction<string>) {
			state.scrollToTestId = payload
		},
		setScrollToGraph(state, { payload }: PayloadAction<string>) {
			state.scrollToGraph = payload
		},
	},
})

export const {
	setIsSubmitting,
	setSelectedRootGroups,
	setSelectedGroups,
	setSelectedType,
	setSelectedTests,
	setSelectedSurveys,
	setSelectedTrainees,
	setSelectedSex,
	setTestOptions,
	setDisableDateRange,
	setStartDate,
	setEndDate,
	setSampleAggregate,
	setShowGroups,
	setShowTrainees,
	resetFormErrors,
	setFormError,
	setSelectedProfileId,
	setScrollToTestId,
	setScrollToGraph,
} = visualizationSlice.actions

const selectVisualizationSlice = (state: RootState) => state.visualization

export const visualizationSelector = createSelector([selectVisualizationSlice], (visualization) => {
	return {
		...visualization,
		curDemo: visualization.targetTableCategory === "ageGroup" ? visualization.targetTableDemo : visualization.targetTableDemoPhv,
	}
})
